import { Link, useParams } from 'react-router-dom';
import LandingNav from '../components/LandingNav';
import Footer from '../components/Footer';
import PageSEO from '../components/PageSEO';
import NotFoundPage from './NotFoundPage';
import { getComparison } from '../data/comparisons';

const BASE_URL = 'https://momentsent.com';

// When the competitor information on these pages was last reviewed. Shown in a
// disclaimer so claims are framed as accurate "as of" a date. Update this when
// the comparison content is re-verified against competitors' public info.
const LAST_REVIEWED = 'June 2026';

/**
 * Head-to-head comparison page: /vs/:slug
 *
 * Data-driven from web/src/data/comparisons.ts. Each page is answer-first
 * (AI search engines quote the intro), honest (names where the competitor
 * genuinely fits better), and emits FAQPage + BreadcrumbList JSON-LD so the
 * content is eligible for rich results and AI citation.
 */
export default function ComparePage() {
  const { slug } = useParams<{ slug: string }>();
  const c = getComparison(slug);

  // Unknown competitor slug → render the standard 404.
  if (!c) return <NotFoundPage />;

  const canonical = `${BASE_URL}/vs/${c.slug}`;

  const structuredData = [
    {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: c.faq.map((f) => ({
        '@type': 'Question',
        name: f.q,
        acceptedAnswer: { '@type': 'Answer', text: f.a },
      })),
    },
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: BASE_URL },
        { '@type': 'ListItem', position: 2, name: 'Compare', item: `${BASE_URL}/vs/${c.slug}` },
        { '@type': 'ListItem', position: 3, name: c.h1, item: canonical },
      ],
    },
  ];

  return (
    <div className="min-h-screen bg-cream flex flex-col">
      <PageSEO
        title={c.metaTitle}
        description={c.metaDescription}
        canonical={canonical}
        structuredData={structuredData}
      />
      <LandingNav />

      {/* ── Hero / answer-first intro ───────────────────────────────────────── */}
      <section className="pt-28 pb-12 px-6">
        <div className="max-w-3xl mx-auto text-center">
          <p className="font-body text-sm text-sage font-semibold tracking-widest uppercase mb-4">
            Comparison
          </p>
          <h1 className="font-heading text-4xl md:text-5xl text-navy leading-tight mb-6">
            {c.h1}
          </h1>
          <p className="font-body text-lg text-navy/80 leading-relaxed">
            {c.intro}
          </p>
          <div className="mt-8">
            <Link to="/signup" className="btn-primary inline-block px-8 py-3 text-base">
              Start your free trial →
            </Link>
            <p className="mt-3 font-body text-xs text-gray-400">
              14-day free trial. No credit card required.
            </p>
          </div>
        </div>
      </section>

      {/* ── Comparison table ────────────────────────────────────────────────── */}
      <section className="py-12 px-6">
        <div className="max-w-3xl mx-auto">
          <div className="overflow-hidden rounded-card shadow-warm border border-gray-100 bg-white">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-navy text-cream">
                  <th className="font-heading text-sm md:text-base p-4 font-semibold">Feature</th>
                  <th className="font-heading text-sm md:text-base p-4 font-semibold">MomentSent</th>
                  <th className="font-heading text-sm md:text-base p-4 font-semibold">{c.competitor}</th>
                </tr>
              </thead>
              <tbody>
                {c.rows.map((row, i) => (
                  <tr key={row.feature} className={i % 2 === 0 ? 'bg-white' : 'bg-cream/40'}>
                    <td className="font-body text-sm text-navy p-4 font-semibold align-top">
                      {row.feature}
                    </td>
                    <td className="font-body text-sm text-gray-700 p-4 align-top">
                      {row.momentsent}
                    </td>
                    <td className="font-body text-sm text-gray-500 p-4 align-top">
                      {row.competitor}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Comparative-advertising disclaimer: frames competitor claims as
              accurate as of a review date and points readers to the source. */}
          <p className="mt-5 font-body text-xs text-gray-400 leading-relaxed text-center">
            Comparison based on publicly available information about {c.competitor} as of {LAST_REVIEWED}.
            Competitor features and pricing may change. Please verify current details on each provider's
            own website. {c.competitor} is a trademark of its respective owner and is referenced here for
            comparison only.
          </p>
        </div>
      </section>

      {/* ── Where MomentSent stands out ─────────────────────────────────────── */}
      <section className="py-10 px-6">
        <div className="max-w-3xl mx-auto">
          <h2 className="font-heading text-2xl text-navy mb-6">Where MomentSent stands out</h2>
          <ul className="space-y-4">
            {c.momentsentWins.map((w) => (
              <li key={w} className="flex gap-3">
                <span className="text-sage font-bold mt-0.5" aria-hidden="true">✓</span>
                <span className="font-body text-base text-gray-600 leading-relaxed">{w}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* ── Where the competitor may fit better (honest) ────────────────────── */}
      <section className="py-10 px-6">
        <div className="max-w-3xl mx-auto">
          <h2 className="font-heading text-2xl text-navy mb-4">Where {c.competitor} may fit better</h2>
          <p className="font-body text-base text-gray-600 leading-relaxed mb-6">
            {c.competitorSummary}
          </p>
          <ul className="space-y-4">
            {c.competitorWins.map((w) => (
              <li key={w} className="flex gap-3">
                <span className="text-gray-400 font-bold mt-0.5" aria-hidden="true">•</span>
                <span className="font-body text-base text-gray-600 leading-relaxed">{w}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* ── Best for ────────────────────────────────────────────────────────── */}
      <section className="py-10 px-6">
        <div className="max-w-3xl mx-auto grid md:grid-cols-2 gap-6">
          <div className="bg-sage/10 rounded-card p-8">
            <h3 className="font-heading text-lg text-navy mb-3">MomentSent is best for</h3>
            <p className="font-body text-base text-gray-600 leading-relaxed">{c.bestFor.momentsent}</p>
          </div>
          <div className="bg-white rounded-card p-8 border border-gray-100">
            <h3 className="font-heading text-lg text-navy mb-3">{c.competitor} is best for</h3>
            <p className="font-body text-base text-gray-600 leading-relaxed">{c.bestFor.competitor}</p>
          </div>
        </div>
      </section>

      {/* ── FAQ ─────────────────────────────────────────────────────────────── */}
      <section className="py-12 px-6">
        <div className="max-w-3xl mx-auto">
          <h2 className="font-heading text-2xl text-navy mb-6">Frequently asked questions</h2>
          <div className="space-y-6">
            {c.faq.map((f) => (
              <div key={f.q}>
                <h3 className="font-heading text-lg text-navy mb-2">{f.q}</h3>
                <p className="font-body text-base text-gray-600 leading-relaxed">{f.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ─────────────────────────────────────────────────────────────── */}
      <section className="py-20 px-6 bg-sage/10">
        <div className="max-w-xl mx-auto text-center">
          <h2 className="font-heading text-3xl text-navy mb-4">
            Never miss a moment again.
          </h2>
          <p className="font-body text-base text-gray-500 leading-relaxed mb-8">
            Set the dates once and MomentSent mails a real card automatically, every year, to the
            people you love.
          </p>
          <Link to="/signup" className="btn-primary inline-block px-8 py-3 text-base">
            Start your free trial →
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
